// Modules
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Utils
import { useAppSelector } from 'app/store';
import { selectAdminUser } from 'features/auth/authSlice';
import { getFirstAccessibleRoute, AccessibleRoute } from 'utils/accessibleRoutes';

export interface UseFirstAccessibleRouteResult {
  /** The first accessible route URL */
  href: string;
  /** The translated label for the route */
  label: string;
  /** The translation key for the route label */
  labelKey: string;
}

/**
 * Hook that returns the first accessible route for the current user
 * based on their permissions and the sidebar navigation order.
 */
export const useFirstAccessibleRoute = (): UseFirstAccessibleRouteResult => {
  const { t } = useTranslation('common');
  const user = useAppSelector(selectAdminUser);

  const accessibleRoute = useMemo((): AccessibleRoute | null => {
    if (!user) return null;
    return getFirstAccessibleRoute(user);
  }, [user]);

  const result = useMemo((): UseFirstAccessibleRouteResult => {
    const href = accessibleRoute?.href ?? '/dashboard';
    const labelKey = accessibleRoute?.labelKey ?? 'SIDEBAR_DASHBOARD';

    return {
      href,
      label: t(labelKey),
      labelKey,
    };
  }, [accessibleRoute, t]);

  return result;
};

export default useFirstAccessibleRoute;

